import { DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Event,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterEvent,
  Scroll,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgProgress } from 'ngx-progressbar';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { CmsService } from './cms/services/cms/cms.service';
import { deduplicateLocale } from './core/helpers/locale/locale.helper';
import { CustomHeadTagService } from './core/services/custom-head-tag/custom-head-tag.service';
import { LoaderService } from './core/services/loader/loader.service';
import { MatchMediaService } from './core/services/match-media/match-media.service';
import { RouterHistoryService } from './core/services/router-history/router-history.service';
import { configuredLang } from '~/i18n/i18n.module';
import { NotFoundComponent } from './attendee/components/shared/not-found/not-found.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly ngProgress: NgProgress,
    private readonly loaderService: LoaderService,
    private readonly route: ActivatedRoute,
    private readonly routerHistoryService: RouterHistoryService,
    private readonly router: Router,
    private readonly viewportScroller: ViewportScroller,
    private readonly translateService: TranslateService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly cmsService: CmsService,
    private readonly customHeadTagService: CustomHeadTagService,
    private readonly matchMediaService: MatchMediaService
  ) {
    translateService.addLangs([configuredLang.toString()]);
    translateService.use(translateService.currentLang);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    if (!this.isNotFoundPage()) {
      this.disableCanReference();
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.canonicalUrl();
        }
      });
      this.subscriptions.add(
        this.route.url.subscribe((event) => {
          this.canonicalUrl();
        })
      );
      this.progressBarSubscription();
      this.routerHistorySubscription();
      this.viewportResizeSubscription();
      this.viewportScrollerSubscription();

      this.document.documentElement.lang = deduplicateLocale(
        this.translateService.currentLang
      );

      this.subscriptions.add(
        this.cmsService.getConfiguration().subscribe(({ design }) => {
          // Favicon //
          if (design?.favicon_32x32) {
            const extension = design.favicon_32x32?.name.split('.').pop();
            const type =
              extension?.toLowerCase() === 'png' ? 'image/png' : 'image/x-icon';

            this.customHeadTagService.addTag('link', {
              rel: 'icon',
              type,
              href: design.favicon_32x32?.url,
            });
          }

          // Apple touch icon //
          if (design?.icon_pwa_192x192) {
            this.customHeadTagService.addTag('link', {
              rel: 'apple-touch-icon',
              href: design.icon_pwa_192x192?.url,
            });
          }
        })
      );
    } else {
      this.customHeadTagService.addTag('link', {
        rel: 'icon',
        type: 'image/png',
        href: '/assets/favicon.png',
      });
    }
  }

  private findComponentInRouteTree(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.component) {
      return snapshot.component;
    }

    for (const child of snapshot.children) {
      const component = this.findComponentInRouteTree(child);
      if (component) {
        return component;
      }
    }

    return null;
  }

  private isNotFoundPage(): boolean {
    const currentRoute = this.router.routerState.snapshot.root;

    return this.findComponentInRouteTree(currentRoute) === NotFoundComponent;
  }

  private canonicalUrl() {
    const head = this.document.getElementsByTagName('head')[0];
    let element: HTMLLinkElement =
      this.document.querySelector("link[rel='canonical']") || null;
    if (element == null) {
      element = this.document.createElement('link');
      head.appendChild(element);
    }
    element.setAttribute('rel', 'canonical');
    if (isPlatformBrowser(this.platformId)) {
      element.setAttribute('href', location.origin + location.pathname);
    }
  }

  private disableCanReference(): void {
    if (
      this.router?.url?.includes('/fr-CA') ||
      this.router?.url?.includes('/en-CA')
    ) {
      this.customHeadTagService.addTag('meta', {
        name: 'robots',
        content: 'noindex, nofollow',
      });
    }
  }

  /**
   * Subscribe to the progressBar element to trigger the
   * loaderService loading state ON or OFF.
   */
  private progressBarSubscription(): void {
    const progressBar = this.ngProgress.ref();

    this.subscriptions.add(
      progressBar.started.subscribe(() => {
        // start loader whenever progress is started
        this.loaderService.setLoading(true);
      })
    );

    this.subscriptions.add(
      progressBar.completed.subscribe(() => {
        // stop loader whenever progress is completed
        this.loaderService.setLoading(false);
      })
    );
  }

  /**
   * Subscribe to router events and push every NavigationStart
   * events to the routerHistoryService. It will store them and
   * make them accessible from anywhere in the app.
   */
  private routerHistorySubscription(): void {
    this.routerHistoryService.push(this.router.url);

    this.subscriptions.add(
      this.router.events.subscribe((event: Event | RouterEvent) => {
        if (event instanceof NavigationStart) {
          this.routerHistoryService.push(event.url);
        }
      })
    );
  }

  private viewportResizeSubscription(): void {
    this.subscriptions.add(this.matchMediaService.resizeHandler().subscribe());
  }

  /**
   * Subscribe to router events Scroll and push the information
   * to the viewportScroller to scroll accordingly.
   */
  private viewportScrollerSubscription(): void {
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event: Event): event is Scroll => event instanceof Scroll)
        )
        .subscribe((event) => {
          if (event.position) {
            // backward navigation
            return this.viewportScroller.scrollToPosition(event.position);
          }

          if (event.anchor) {
            // anchor navigation
            return this.viewportScroller.scrollToAnchor(event.anchor);
          }

          // forward navigation
          return this.viewportScroller.scrollToPosition([0, 0]);
        })
    );
  }
}
